import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "block text-sm font-medium text-gray-700 mb-1" }
const _hoisted_2 = {
  key: 0,
  class: "text-red-500"
}
const _hoisted_3 = { class: "flex" }
const _hoisted_4 = ["value", "placeholder", "required", "disabled"]
const _hoisted_5 = {
  key: 0,
  class: "mt-1 text-sm text-red-600"
}
const _hoisted_6 = {
  key: 1,
  class: "mt-1 text-xs text-gray-500"
}

import { computed, ref, watch } from 'vue';
import type { PhoneFieldProps } from '@/types/sellerRegister';


export default /*@__PURE__*/_defineComponent({
  __name: 'PhoneField',
  props: {
    label: {},
    required: { type: Boolean },
    placeholder: {},
    modelValue: {},
    error: { type: Boolean },
    disabled: { type: Boolean },
    gridClass: {},
    validationType: {}
  },
  emits: ["update:modelValue", "validation-changed"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;
// État local pour la validation
const errorMessage = ref('');
const hasBeenTouched = ref(false);

// Classes CSS pour l'input
const inputClasses = computed(() => {
  const baseClasses = 'focus:outline-none';
  
  if (errorMessage.value) {
    return `${baseClasses} border-red-500`;
  } else if (props.modelValue && isValidPhone.value) {
    return `${baseClasses} border-green-500`;
  } else {
    return `${baseClasses} border-gray-300`;
  }
});

// Validation du numéro de téléphone français
const isValidPhone = computed(() => {
  if (!props.modelValue) return false;
  
  const phoneRegex = /^0[1-9](\d{8})$/;
  
  switch (props.validationType) {
    case 'phone':
    case 'mobile':
      // Format strict français : 10 chiffres commençant par 0
      return phoneRegex.test(props.modelValue);
    
    case 'fax':
      // Fax : format français OU vide (optionnel)
      if (props.modelValue === '') return true; // Vide autorisé
      return phoneRegex.test(props.modelValue);
    
    default:
      // Par défaut : validation stricte
      return phoneRegex.test(props.modelValue);
  }
});

// Validation complète avec messages d'erreur
const validatePhone = () => {
  if (!props.modelValue) {

    if (props.validationType === 'fax') {
      errorMessage.value = '';
      return true;
    }
    errorMessage.value = '';
    return true;
  }
  
  if (props.modelValue.length < 10) {
    errorMessage.value = 'Le numéro doit contenir 10 chiffres';
    return false;
  }
  
  if (!isValidPhone.value) {
    errorMessage.value = 'Format invalide. Utilisez le format français (ex: 0123456789)';
    return false;
  }
  
  errorMessage.value = '';
  return true;
};

// Gérer les changements d'input
const onInput = (event: Event) => {
  const target = event.target as HTMLInputElement;
  const inputValue = target.value;
  
  // Vérifier si l'input contient des caractères non-numériques
  if (/\D/.test(inputValue)) {
    target.value = props.modelValue;
    return;
  }

  let value = inputValue;
  
  // Bloquer à 10 chiffres maximum
  if (value.length > 10) {
    target.value = props.modelValue;
    return;
  }
  
  emit('update:modelValue', value);
  
  // Valider et émettre l'état
  const isValid = validatePhone();
  emit('validation-changed', isValid);
};

// Gérer la perte de focus
const onBlur = () => {
  hasBeenTouched.value = true;
  
  // Valider seulement si le champ a été touché ou s'il y a une valeur
  if (hasBeenTouched.value || props.modelValue) {
    const isValid = validatePhone();
    emit('validation-changed', isValid);
  }
};

// Watcher pour la validation externe
watch(() => props.modelValue, (newValue) => {
  if (hasBeenTouched.value || newValue) {
    validatePhone();
  }
}, { immediate: true });

// Watcher pour la prop error externe
watch(() => props.error, (newError) => {
  if (newError && !errorMessage.value) {
    errorMessage.value = 'Numéro de téléphone invalide';
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(_ctx.gridClass)
  }, [
    _createElementVNode("label", _hoisted_1, [
      _createTextVNode(_toDisplayString(_ctx.label) + " ", 1),
      (_ctx.required)
        ? (_openBlock(), _createElementBlock("span", _hoisted_2, "*"))
        : _createCommentVNode("", true)
    ]),
    _createElementVNode("div", _hoisted_3, [
      _cache[0] || (_cache[0] = _createElementVNode("div", { class: "flex items-center px-3 py-2 bg-gray-50 border border-r-0 border-gray-300 rounded-l-lg text-sm text-gray-600 font-medium" }, " 🇫🇷 +33 ", -1)),
      _createElementVNode("input", {
        value: _ctx.modelValue,
        onInput: onInput,
        onBlur: onBlur,
        type: "tel",
        placeholder: _ctx.placeholder,
        required: _ctx.required,
        disabled: _ctx.disabled,
        class: _normalizeClass(["flex-1 px-3 py-2 border rounded-r-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors text-sm", inputClasses.value]),
        inputmode: "numeric",
        autocomplete: "tel"
      }, null, 42, _hoisted_4)
    ]),
    (errorMessage.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(errorMessage.value), 1))
      : _createCommentVNode("", true),
    (_ctx.modelValue)
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(_ctx.modelValue.length) + "/10 caractères ", 1))
      : _createCommentVNode("", true)
  ], 2))
}
}

})